<template>
  <div class="relative flex-1">
    <div id="line-chart-wrapper" class="line-chart-wrapper">
      <PriceAssessmentsMultiAxisChart
        :key="updatedPriceChart"
        data-testid="price-assessments-chart"
        :chart-data="chartData"
        :title="titlePage"
        :has-price-axis="hasPriceAxis"
        :has-payable-axis="hasPayableAxis"
        :show-payable-axis="showPayableAxis"
        :show-price-axis="showPriceAxis"
        :y-axis-label="axisY1"
        :y-axis1-label="axisY2"
        :categories="chartData.categories"
        :replace-legend="replaceLegend"
        class="h-[500px] w-full lg:left-0 lg:top-0 lg:pr-10"
        :plugins="plugins"
        :min-y1="minMaxY1.min"
        :max-y1="minMaxY1.max"
        :min-y2="minMaxY2.min"
        :max-y2="minMaxY2.max"
        :market="market"
      />
    </div>
    <div v-show="!hideLegend" id="legend-container" class="mt-4" />
  </div>
</template>

<script setup lang="ts">
import CrosshairPlugin from 'chartjs-plugin-crosshair'
import { reformat } from '~/utils/date'
import PriceAssessmentsMultiAxisChart from '~/components/price-assessments/MultiAxisChart.vue'
import { EventBus } from '~/utils/eventBus'
import { generatePriceLegends } from '~/utils/chart/customLegends/priceLegends'
import {
  createDataset,
  extractDates,
  getCategoriesByProducts,
  getMinMax,
  getNewColor,
  getProductByRawData,
  getValidCategories,
  orderDataset,
} from '~/utils/chart-container'

// Props
interface Props {
  rawData: Record<string, any>
  title: string
  numberDigits?: number
  yAxisLabel?: string
  categoriesOrder: string[]
  replaceLegend: boolean
  hashes: string[]
  market?: string
}

const MIN_MAX_DEFAULT = {
  min: 0,
  max: null,
}

const plugins = [generatePriceLegends, CrosshairPlugin]

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'setDatasetVisibility', payload: { hashes: string[] }): void
}>()

// Data
const updatedPriceChart = ref(0)
const dates = ref<string[]>([])
const productPriceDatasets = ref<any[]>([])
const productPayableDatasets = ref<any[]>([])
const categories = ref<any[]>([])
const addEvent = ref(false)
const element = ref<HTMLElement | undefined>(undefined)
const hideLegend = ref(false)

// Computed
const hasPriceAxis = computed(() => !!productPriceDatasets.value?.length)
const hasPayableAxis = computed(() => !!productPayableDatasets.value?.length)
const productNamePayable = computed(
  () => props.rawData?.y2?.data[0]?.Product?.ProductName,
)

const titlePage = computed(() => {
  if (!dates.value || dates.value.length === 0) return ''

  const startDate = dates.value[0]
  const endDate = dates.value[dates.value.length - 1]

  return `${props.title} Prices: ${reformat(startDate, 'MMMM yyyy')} - ${reformat(endDate, 'MMMM yyyy')}`
})

const chartData = computed(() => {
  const labelPrefix =
    hasPriceAxis.value && productNamePayable.value === 'Nickel' ? '(RHS) ' : ''
  let colorIndex = 0

  const y1 = productPriceDatasets.value.map((datasetInfo) => {
    const borderColor = getNewColor(colorIndex)
    colorIndex++
    return createDataset({
      datasetInfo,
      yAxisID: 'y1',
      digits: props.numberDigits,
      borderColor,
    })
  })

  const y2 =
    productPayableDatasets.value?.map((datasetInfo) => {
      const borderColor = getNewColor(colorIndex)
      colorIndex++
      return createDataset({
        datasetInfo,
        yAxisID: 'y2',
        labelPrefix,
        digits: props.numberDigits,
        borderColor,
      })
    }) || []

  const datasets = orderDataset([...y1, ...y2], props.categoriesOrder)

  return {
    categories: categories.value,
    labels: dates.value.filter(Boolean),
    datasets,
  }
})

const rawDataY1 = computed(() => {
  if (!props.rawData) return null
  return getRawDataByTpe('y1')
})

const rawDataY2 = computed(() => {
  if (!props.rawData) return null
  return getRawDataByTpe('y2')
})

const axisY1 = computed(() => {
  if (rawDataY1.value === null) return 'USD/tonne'

  const { currency, unitOfMeasure } = rawDataY1.value

  return currency === null
    ? `${unitOfMeasure.symbol} Payable`
    : `${currency?.iso}/${unitOfMeasure.name}`
})

const axisY2 = computed(() => {
  if (rawDataY2.value === null) return '% Payable'

  const { currency, unitOfMeasure } = rawDataY2.value

  return currency === null
    ? `${unitOfMeasure.symbol} Payable`
    : `${currency?.iso}/${unitOfMeasure.name}`
})

const showPayableAxis = computed(() =>
  Object.values(props.rawData)
    .map((item) => item?.data || [])
    .flat()
    .filter((item) => item.unitOfMeasure?.name === 'Percentage')
    .map((item) => item?.hash)
    .some((item) => props.hashes.includes(item)),
)

const priceUnitOfMeasures = ['Tonne', 'kWh']

const showPriceAxis = computed(() =>
  Object.values(props.rawData)
    .map((item) => item?.data || [])
    .flat()
    .filter((item) => priceUnitOfMeasures.includes(item.unitOfMeasure?.name))
    .map((item) => item?.hash)
    .some((item) => props.hashes.includes(item)),
)

const minMaxY1 = computed(() => {
  if (rawDataY1.value === null) return MIN_MAX_DEFAULT

  const { currency } = rawDataY1.value

  if (currency !== null) return MIN_MAX_DEFAULT

  return getMinMax(productPriceDatasets.value)
})

const minMaxY2 = computed(() => {
  if (rawDataY2.value === null) return MIN_MAX_DEFAULT

  const { currency } = rawDataY2.value

  if (currency !== null) return MIN_MAX_DEFAULT

  return getMinMax(productPayableDatasets.value)
})

// Methods
function generateGraph() {
  resetGraphData()

  hideLegend.value = rawDataY1.value?.hideChartComponent
  hideLegend.value = rawDataY2.value?.hideChartComponent

  dates.value = extractDates(props.rawData)

  productPriceDatasets.value = getProductByRawData(rawDataY1.value, dates.value)
  categories.value = getCategoriesByProducts(
    categories.value,
    productPriceDatasets.value,
    rawDataY1.value?.unitOfMeasure,
  )

  productPayableDatasets.value = getProductByRawData(
    rawDataY2.value,
    dates.value,
  )
  categories.value = getCategoriesByProducts(
    categories.value,
    productPayableDatasets.value,
    rawDataY2.value?.unitOfMeasure,
  )

  const allData = Object.values(props.rawData)
    .filter((item: any) => item?.data)
    .flatMap((item: any) => item.data)
  const hashes = allData.filter((item) => !item.hidden).map((item) => item.hash)

  emit('setDatasetVisibility', { hashes })

  categories.value = getValidCategories(allData, categories.value)

  updatedPriceChart.value++
}

function resetGraphData() {
  dates.value = []
  productPriceDatasets.value = []
  productPayableDatasets.value = []
}

const getRawDataByTpe = (type: string) => {
  const rawData = props.rawData[type]
  if (rawData === undefined) return null
  return rawData
}

function mouseEnterChart() {
  EventBus.$emit('mouseonchart', true)
  EventBus.$emit('on-chart-hover')
}

function mouseLeaveChart() {
  EventBus.$emit('mouseonchart', false)
}

// Watchers
watch(() => props.rawData, generateGraph)

// Lifecycle hooks
onMounted(() => {
  generateGraph()
  nextTick(() => {
    element.value = document.getElementById('line-chart-wrapper') as HTMLElement
    if (!addEvent.value && element.value) {
      element.value.addEventListener('mouseenter', mouseEnterChart, {
        passive: true,
      })
      element.value.addEventListener('mouseleave', mouseLeaveChart, {
        passive: true,
      })
      addEvent.value = true
    }
  })
})

onBeforeUnmount(() => {
  const options: AddEventListenerOptions = { passive: true }
  if (addEvent.value && element.value) {
    element.value.removeEventListener('mouseenter', mouseEnterChart, options)
    element.value.removeEventListener('mouseleave', mouseLeaveChart, options)
  }
})
</script>
